import type { ToolResultItem } from '@elara/client-core/chat/tool-badges/types';

import type { IconName } from '#ui/MaterialIcon.js';

import { AppImage } from '#ui/AppImage.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

import { FaviconImage } from './FaviconImage.js';
import { ROW_ENRICHERS } from './row-enrichers/index.js';

import { PreviewLink } from '../PreviewLink.js';

function hostnameFromUrl(url: string): string | undefined {
  try {
    return new URL(url).hostname;
  } catch {
    return undefined;
  }
}

/**
 * Renders a single row in an expandable tool-result list. When the item has a
 * `previewType`, looks up the matching row enricher and delegates title/subtitle/icon
 * resolution to it (the enricher's `useModel` hook subscribes to the Firestore-backed
 * model for richer data than what the tool output provides). For non-previewable
 * items (web search results), renders an external `<a>` with the tool's favicon.
 */
export function ToolResultRow({ item, itemIcon }: { item: ToolResultItem; itemIcon: IconName }) {
  const enricher = item.previewType ? ROW_ENRICHERS[item.previewType] : undefined;
  const model = enricher?.useModel(item.id);

  const title = enricher ? enricher.resolveTitle({ model, item }) : item.title;
  const subtitle = enricher?.resolveSubtitle ? enricher.resolveSubtitle({ model, item }) : item.subtitle;

  const host = item.href ? hostnameFromUrl(item.href) : undefined;
  const rightLabel = host ?? (subtitle && subtitle !== title ? subtitle : undefined);

  const leadingIcon =
    enricher?.renderIcon?.({ model, item, toolIcon: item.iconName ?? itemIcon }) ?? defaultLeadingIcon(item, itemIcon);

  const content = (
    <div className="flex items-center gap-3 px-2 py-2 min-w-0 text-xs font-normal">
      {leadingIcon}
      <span className="truncate flex-1 text-foreground">{title}</span>
      {rightLabel && <span className="truncate text-muted-foreground shrink-0 max-w-[40%]">{rightLabel}</span>}
    </div>
  );

  return (
    <li className="list-none p-0 m-0 hover:bg-background-secondary rounded-sm transition-colors">
      {item.previewType && item.id ? (
        <PreviewLink previewType={item.previewType} id={item.id} displayShortlistIcon={false}>
          {content}
        </PreviewLink>
      ) : item.href ? (
        <a href={item.href} target="_blank" rel="noopener noreferrer" className="block no-underline">
          {content}
        </a>
      ) : (
        content
      )}
    </li>
  );
}

/** Fallback icon renderer when the enricher doesn't provide one. Handles iconUrl
 *  (logo or thumbnail variant) and plain iconName/toolIcon. */
function defaultLeadingIcon(item: ToolResultItem, toolIcon: IconName): React.ReactNode {
  if (item.iconUrl) {
    if (item.iconVariant === 'thumbnail') {
      return (
        <div className="shrink-0 w-4 h-4 aspect-square rounded-sm overflow-hidden bg-muted">
          <AppImage src={item.iconUrl} alt="" className="w-full h-full object-cover" />
        </div>
      );
    }
    return <FaviconImage src={item.iconUrl} />;
  }
  return (
    <MaterialIcon icon={item.iconName ?? toolIcon} size={IconSizes.xs} className="shrink-0 text-muted-foreground" />
  );
}
